import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, IconButton, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { ISyncQueueItem } from "@home-health-notify/models";
import SyncQueueStatusBadge from "./SyncQueueStatusBadge";
import {
  Callout,
  DetailGrid,
  DetailKey,
  DetailValue,
  DrawerBody,
  DrawerFooter,
  DrawerHeaderRow,
  DrawerMeta,
  DrawerPanel,
  DrawerTitle,
  Scrim,
  Section,
  SectionTitle,
  ServiceItem,
} from "./EmrPatients.style";
import {
  addressLine,
  formatDateTime,
  patientName,
  patientPath,
} from "./syncQueueUtils";

type SyncQueueDrawerProps = {
  item: ISyncQueueItem;
  orgId: string;
  isActing: boolean;
  onClose: () => void;
  onApprove: (item: ISyncQueueItem) => void;
  onReject: (item: ISyncQueueItem) => void;
  onFixAndCreate: (item: ISyncQueueItem) => void;
};

export default function SyncQueueDrawer({
  item,
  orgId,
  isActing,
  onClose,
  onApprove,
  onReject,
  onFixAndCreate,
}: SyncQueueDrawerProps) {
  const { payload } = item;
  const patientInfo = payload?.patientInfo;
  const services = payload?.servicesToPatient ?? [];
  const isPending = item.status === "pending";
  const candidates = item.errorDetail?.candidates ?? [];

  return (
    <>
      <Scrim data-testid="drawer-scrim" onClick={onClose} />
      <DrawerPanel data-testid="sync-queue-drawer">
        <DrawerHeaderRow>
          <div>
            <DrawerTitle>{patientName(item)}</DrawerTitle>
            <DrawerMeta>
              <SyncQueueStatusBadge status={item.status} />
              <span>MRN {item.emrId}</span>
              <span style={{ textTransform: "capitalize" }}>
                {item.syncMode}
              </span>
            </DrawerMeta>
          </div>
          <IconButton size="small" onClick={onClose} data-testid="drawer-close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DrawerHeaderRow>

        <DrawerBody>
          {(item.status === "added" || item.status === "skipped") &&
            item.hhnEntityId && (
              <Section>
                <Callout
                  sx={theme => ({
                    backgroundColor: `${theme.palette.primary.main}1a`,
                    color: theme.palette.primary.dark,
                  })}
                >
                  {item.status === "added"
                    ? "Created in HHN."
                    : "This patient already exists in HHN."}{" "}
                  <Link
                    component={RouterLink}
                    to={patientPath(orgId, item.hhnEntityId)}
                    data-testid="drawer-view-patient"
                  >
                    View patient
                    <OpenInNewIcon
                      fontSize="inherit"
                      sx={{ verticalAlign: "middle", ml: 0.5 }}
                    />
                  </Link>
                </Callout>
              </Section>
            )}

          {patientInfo && (
            <Section>
              <SectionTitle>Patient Details</SectionTitle>
              <DetailGrid>
                <DetailKey>Name</DetailKey>
                <DetailValue>{patientName(item)}</DetailValue>
                <DetailKey>Date of Birth</DetailKey>
                <DetailValue>{patientInfo.dob || "—"}</DetailValue>
                <DetailKey>Phone</DetailKey>
                <DetailValue>{patientInfo.phoneNo || "—"}</DetailValue>
                <DetailKey>Address</DetailKey>
                <DetailValue>{addressLine(item) || "—"}</DetailValue>
                <DetailKey>Patient Status</DetailKey>
                <DetailValue>{payload?.patientStatus ?? "—"}</DetailValue>
              </DetailGrid>
            </Section>
          )}

          {services.length > 0 && (
            <Section>
              <SectionTitle>Services</SectionTitle>
              {services.map(service => (
                <ServiceItem key={service.shorthand || service.name}>
                  <span>
                    {service.name}
                    {service.shorthand ? ` (${service.shorthand})` : ""}
                  </span>
                  <Typography variant="body2" color="text.secondary">
                    {service.status}
                  </Typography>
                </ServiceItem>
              ))}
            </Section>
          )}

          {item.status === "error" && item.errorDetail && (
            <Section>
              <SectionTitle>Error</SectionTitle>
              <Callout
                data-testid="drawer-error"
                sx={theme => ({
                  backgroundColor: `${theme.palette.error.main}1a`,
                  color: theme.palette.error.dark,
                })}
              >
                <strong>{item.errorDetail.reason}</strong>
                {item.errorDetail.errors.length > 0 && (
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {item.errorDetail.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                )}
              </Callout>
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 1.5 }}
                onClick={() => onFixAndCreate(item)}
                data-testid="drawer-fix-and-create"
              >
                Complete details &amp; create patient
              </Button>
            </Section>
          )}

          {item.status === "rejected" && (
            <Section>
              <SectionTitle>Rejection</SectionTitle>
              <Callout
                data-testid="drawer-review-comment"
                sx={theme => ({
                  backgroundColor: theme.palette.background.paper,
                })}
              >
                {item.reviewComment || "—"}
              </Callout>
              {(item.reviewedBy || item.reviewedAt) && (
                <DetailGrid sx={{ marginTop: "12px" }}>
                  <DetailKey>Reviewed by</DetailKey>
                  <DetailValue>{item.reviewedBy || "—"}</DetailValue>
                  {item.reviewedAt && (
                    <>
                      <DetailKey>When</DetailKey>
                      <DetailValue>
                        {formatDateTime(item.reviewedAt)}
                      </DetailValue>
                    </>
                  )}
                </DetailGrid>
              )}
            </Section>
          )}

          {item.status === "blocked_on_mapping" && (
            <Section>
              <SectionTitle>Blocked</SectionTitle>
              <Callout
                data-testid="drawer-blocked-reason"
                sx={theme => ({
                  backgroundColor: `${theme.palette.warning.main}1a`,
                  color: theme.palette.warning.dark,
                })}
              >
                {item.blockedReason || "—"}
              </Callout>
              {candidates.length > 0 && (
                <DetailGrid sx={{ marginTop: "12px" }}>
                  <DetailKey>Possible matches</DetailKey>
                  <div>
                    {candidates.map(id => (
                      <div key={id}>
                        <Link
                          component={RouterLink}
                          to={patientPath(orgId, id)}
                          data-testid="drawer-candidate-link"
                        >
                          View candidate
                          <OpenInNewIcon
                            fontSize="inherit"
                            sx={{ verticalAlign: "middle", ml: 0.5 }}
                          />
                        </Link>
                      </div>
                    ))}
                  </div>
                </DetailGrid>
              )}
            </Section>
          )}

          {item.status === "added" && (item.reviewedBy || item.reviewedAt) && (
            <Section>
              <SectionTitle>Reviewed</SectionTitle>
              <DetailGrid>
                <DetailKey>By</DetailKey>
                <DetailValue>{item.reviewedBy || "—"}</DetailValue>
                {item.reviewedAt && (
                  <>
                    <DetailKey>When</DetailKey>
                    <DetailValue>{formatDateTime(item.reviewedAt)}</DetailValue>
                  </>
                )}
              </DetailGrid>
            </Section>
          )}
        </DrawerBody>

        {isPending && (
          <DrawerFooter>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={isActing}
              onClick={() => onApprove(item)}
              data-testid="drawer-approve"
            >
              Approve &amp; create in HHN
            </Button>
            <Button
              variant="outlined"
              color="error"
              disabled={isActing}
              onClick={() => onReject(item)}
              data-testid="drawer-reject"
            >
              Reject
            </Button>
          </DrawerFooter>
        )}
      </DrawerPanel>
    </>
  );
}
